import { FC, useState } from 'react';
import { createUseStyles } from 'react-jss';
import dayjs from 'dayjs';
import { IonItem, IonLabel } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { ILightCareMap } from '../caremap/ICareMap';
import { CareMapState } from '../caremap/CareMapState';
import { MemberAvatar } from '../household/MemberAvatar';
import { ChevronRightIcon } from '../theme/Icons';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { InsightSummaryContent } from '../insights/InsightSummaryContent';
import { IClientInsight, OrganizationInsightsState } from './OrganizationInsightsState';

// A caseload row has to stay scannable down a long list, so a client leads with the single thing most
// worth opening them for and keeps the rest one tap away.
const MAX_INLINE_OBSERVATIONS = 1;

const useStyles = createUseStyles({
    identity: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        minWidth: 0
    },

    name: {
        fontSize: 15,
        fontWeight: 500
    },

    activity: {
        fontSize: 12.5,
        color: 'var(--ion-color-step-600)',
        marginTop: 2
    },

    // Indented to the client's name rather than the card edge, so an observation reads as belonging
    // to the person above it instead of as another item in the caseload.
    observations: {
        padding: '0 0 8px 68px'
    },

    observation: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 16px',
        alignItems: 'start',
        gap: 8,
        padding: '7px 10px',
        borderRadius: 8,
        cursor: 'pointer',
        // smaller than a home insight row: nested under a client, it is a detail of that row
        fontSize: 13.5,

        '&:hover': {
            backgroundColor: 'var(--ion-color-step-50)'
        }
    },

    chevron: {
        paddingTop: 3
    },

    more: {
        background: 'none',
        border: 0,
        padding: '4px 10px',
        font: 'inherit',
        fontSize: 13,
        color: 'var(--ion-color-primary)',
        cursor: 'pointer'
    }
});

export interface IOrganizationClientCareMapRowProps {
    careMap: ILightCareMap;
    disabled: boolean;
    onClick: (careMap: ILightCareMap) => void;
}

/**
 * One care recipient in an agency's caseload: who they are, and what Bea has outstanding on them.
 * The observations sit under the client rather than in a list of their own, because a case manager
 * works client by client, and a flat list of findings makes a client with two of them appear twice.
 */
export const OrganizationClientCareMapRow: FC<IOrganizationClientCareMapRowProps> = ({ careMap, disabled, onClick }) => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const insightsState = useInstance(OrganizationInsightsState);
    useSubscription(() => insightsState);

    const history = useInstance(HistoryViewModel);

    const [showAllObservations, setShowAllObservations] = useState(false);

    const observations = insightsState.getItemsForCareMap(careMap.careMapId);
    const visible = showAllObservations ? observations : observations.slice(0, MAX_INLINE_OBSERVATIONS);
    // Counted against the collapsed list rather than what is on screen, so the control still has a
    // label to show once everything is expanded and can put them back.
    const extraCount = Math.max(observations.length - MAX_INLINE_OBSERVATIONS, 0);

    const latestActivity = insightsState.getLatestActivity(careMap.careMapId);

    const openObservation = (entry: IClientInsight) => {
        const searchParams = new URLSearchParams();
        searchParams.set('drawer', JSON.stringify({
            mode: 'view-insight',
            insightId: entry.insight.insightId,
            surface: 'agency-home'
        }));
        history.push(`/insights/${entry.careMapId}?${searchParams.toString()}`);
    };

    return (
        <>
            <IonItem
                button={!disabled}
                data-id={careMap.careMapId}
                onClick={disabled ? undefined : () => onClick(careMap)}
                lines="none"
            >
                <IonLabel>
                    <div className={classes.identity}>
                        <MemberAvatar
                            size={40}
                            member={{ photo: careMap.patient.photo, color: careMap.patient.color, avatar: careMap.patient.avatar }}
                        />
                        <div>
                            <div className={`${classes.name} mp-mask`}>
                                {careMapState.getPatientFullName(careMap) || 'New Care Recipient'}
                            </div>
                            {latestActivity && (
                                <div className={classes.activity}>Latest activity {dayjs(latestActivity).format('MM/DD/YY')}</div>
                            )}
                        </div>
                    </div>
                </IonLabel>
            </IonItem>

            {visible.length > 0 && (
                <div className={classes.observations} data-type="client-observations">
                    {/* The rows are not buttons: the summary is a shared component that renders
                        block elements, which a button may not contain. Nothing inside a row is
                        interactive, so the role costs nothing here even though it makes its
                        children presentational. */}
                    {visible.map(entry => (
                        <div
                            key={entry.insight.insightId}
                            className={classes.observation}
                            data-type="client-observation"
                            data-insight-id={entry.insight.insightId}
                            role="button"
                            tabIndex={0}
                            onClick={() => openObservation(entry)}
                            onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    openObservation(entry);
                                }
                            }}
                        >
                            <div>
                                <InsightSummaryContent insight={entry.insight} />
                            </div>
                            <div className={classes.chevron}>
                                <ChevronRightIcon color="medium" size="small" />
                            </div>
                        </div>
                    ))}

                    {extraCount > 0 && (
                        <button
                            className={classes.more}
                            data-id="toggle-client-observations"
                            aria-expanded={showAllObservations}
                            onClick={() => setShowAllObservations(!showAllObservations)}
                        >
                            {showAllObservations
                                ? 'Show less'
                                : extraCount === 1 ? '1 more' : `${extraCount} more`
                            }
                        </button>
                    )}
                </div>
            )}
        </>
    );
};
